/**
 * <nav-bar> - Barre de navigation principale
 */

import { navigateTo, getCurrentPath } from '@/lib/router.js';
import { subscribe, getState, setState } from '@/lib/store.js';
import { signOut } from '@/lib/supabaseClient.js';
import { t } from '@/lib/i18n.js';
import logoEdena from '@/assets/images/logoEdenaTransparent.png';
import { getOffresActives } from '@/lib/api.js';
import {
  getBookingSummaryStyles,
  renderBookingSummaryOverlay,
  buildBookingSummaryData,
  getOfferSummaryMessage,
} from './booking-summary-ui.js';
import { getOfferSelectionState } from './booking-offer.js';

class NavBar extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this._showCartOverlay = false;
    this.activeOffre = null;
    this.activeOffres = [];
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
    this.loadActiveOffre();

    // S'abonner aux changements d'état qui nécessitent un re-render
    this.unsubscribeAuth = subscribe('isAuthenticated', () => this.render());
    this.unsubscribeProfile = subscribe('userProfile', () => this.render());
    this.unsubscribeRole = subscribe('userRole', () => this.render());
    this.unsubscribeCartPrestation = subscribe('currentPrestation', () => this.render());
    this.unsubscribeCartPrestations = subscribe('currentPrestations', () => this.render());
    this.unsubscribeCurrentSite = subscribe('currentSite', () => this.loadActiveOffre());

    // Mettre à jour l'état actif lors des changements de route
    window.addEventListener('hashchange', () => this.updateActiveLink());

    // Fermer le menu mobile lorsqu'on clique en dehors du composant
    window.addEventListener('click', this.handleOutsideClick);
  }

  disconnectedCallback() {
    if (this.unsubscribeAuth) this.unsubscribeAuth();
    if (this.unsubscribeProfile) this.unsubscribeProfile();
    if (this.unsubscribeRole) this.unsubscribeRole();
    if (this.unsubscribeCartPrestation) this.unsubscribeCartPrestation();
    if (this.unsubscribeCartPrestations) this.unsubscribeCartPrestations();
    if (this.unsubscribeCurrentSite) this.unsubscribeCurrentSite();
    window.removeEventListener('click', this.handleOutsideClick);
  }

  async loadActiveOffre() {
    const { currentSite } = getState();
    if (!currentSite?.id) {
      this.activeOffre = null;
      return;
    }

    const { data: offres, error } = await getOffresActives(currentSite.id);
    if (error) {
      console.warn('Erreur chargement offres actives:', error);
      this.activeOffre = null;
      this.activeOffres = [];
      return;
    }

    this.activeOffres = offres || [];
    this.activeOffre = this.activeOffres[0] || null;
    this.render();
  }

  setupEventListeners() {
    this.shadowRoot.addEventListener('click', (e) => {
      const cartBtn = e.target.closest('[data-action="open-cart"]');
      if (cartBtn) {
        e.preventDefault();
        const snapshot = this.getCartSnapshot();
        const prestations = Array.isArray(snapshot.prestations) ? snapshot.prestations : [];
        if (!prestations.length) {
          return;
        }
        this._showCartOverlay = true;
        this.render();
        return;
      }

      const overlay = e.target.closest('.booking-summary-overlay');
      if (overlay && overlay === e.target) {
        this._showCartOverlay = false;
        this.render();
        return;
      }

      const closeSummary = e.target.closest('[data-action="close-summary"]');
      if (closeSummary) {
        this._showCartOverlay = false;
        this.render();
        return;
      }

      const cartRemoveBtn = e.target.closest('[data-action="cart-remove"]');
      if (cartRemoveBtn) {
        e.preventDefault();
        e.stopPropagation();
        const prestationId = cartRemoveBtn.dataset.prestationId;
        if (!prestationId) return;

        const snapshot = this.getCartSnapshot();
        const prestations = Array.isArray(snapshot.prestations) ? snapshot.prestations : [];
        const offerRefDate = snapshot?.creneau?.debut || undefined;
        const { offerInfo, autoAddedOfferedIds } = getOfferSelectionState(
          prestations,
          this.activeOffre,
          offerRefDate
        );
        if (autoAddedOfferedIds?.has(String(prestationId))) {
          this.rememberOfferExclusion(prestationId, offerInfo);
        }
        const remaining = prestations.filter((p) => String(p?.id) !== String(prestationId));

        if (remaining.length === 0) {
          this._showCartOverlay = false;
          setState({ currentPrestation: null, currentPrestations: null });
          try { window.sessionStorage.removeItem('bookingWizardStep'); } catch {}
        } else {
          setState({
            currentPrestation: remaining[0] || null,
            currentPrestations: remaining,
          });
          try {
            const raw = window.sessionStorage.getItem('bookingWizardStep');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && Array.isArray(parsed.prestations)) {
                parsed.prestations = remaining;
                window.sessionStorage.setItem('bookingWizardStep', JSON.stringify(parsed));
              }
            }
          } catch {}
        }

        this.render();
        return;
      }

      const cartContinueBtn = e.target.closest('[data-action="cart-continue"]');
      if (cartContinueBtn) {
        this._showCartOverlay = false;
        this.render();
        try {
          window.sessionStorage.setItem('skipSummaryOpenOnce', '1');
        } catch {}
        const hash = window.location.hash || '';
        const target = hash.startsWith('#/services?') ? hash.slice(1) : '/services';
        navigateTo(target);
        return;
      }

      const cartBookBtn = e.target.closest('[data-action="cart-book"]');
      if (cartBookBtn) {
        const snapshot = this.getCartSnapshot();
        const prestations = Array.isArray(snapshot.prestations) ? snapshot.prestations : [];
        if (!prestations.length) return;
        setState({
          currentPrestation: prestations[0] || null,
          currentPrestations: prestations,
        });
        this._showCartOverlay = false;
        this.render();
        navigateTo('/booking');
        return;
      }

      const link = e.target.closest('[data-route]');
      if (link) {
        e.preventDefault();
        const route = link.dataset.route;
        navigateTo(route);
      }

      const logoutBtn = e.target.closest('[data-action="logout"]');
      if (logoutBtn) {
        this.handleLogout();
      }

      const menuToggle = e.target.closest('.menu-toggle');
      if (menuToggle) {
        this.toggleMobileMenu();
      }
    });
  }

  getCartSnapshot() {
    const { currentPrestation, currentPrestations } = getState();

    const fromStore = Array.isArray(currentPrestations) && currentPrestations.length > 0
      ? currentPrestations
      : currentPrestation
        ? [currentPrestation]
        : [];

    let fromSession = null;
    try {
      const raw = window.sessionStorage.getItem('bookingWizardStep');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.prestations) && parsed.prestations.length > 0) {
          fromSession = parsed;
        }
      }
    } catch {}

    const prestations = fromStore.length > 0
      ? fromStore
      : Array.isArray(fromSession?.prestations)
        ? fromSession.prestations
        : [];

    const withoutPreference = fromSession?.withoutPreference === true || fromSession?.withoutPreference === false
      ? fromSession.withoutPreference
      : true;

    const employe = withoutPreference ? null : (fromSession?.employe || null);
    const creneau = fromSession?.creneau || null;

    return {
      prestations,
      employe,
      withoutPreference,
      creneau,
    };
  }

  getCartSummaryData() {
    const snapshot = this.getCartSnapshot();
    const prestations = Array.isArray(snapshot.prestations) ? snapshot.prestations : [];

    let totalMinutes = 0;
    let totalCents = 0;
    let hasTotalPrice = true;

    prestations.forEach((p) => {
      const mins = p?.duree_min ? parseInt(p.duree_min, 10) : 0;
      if (Number.isFinite(mins) && mins > 0) {
        totalMinutes += mins;
      }

      const cents = p?.prix_centimes_public !== null && p?.prix_centimes_public !== undefined
        ? parseInt(p.prix_centimes_public, 10)
        : null;
      if (Number.isFinite(cents) && cents >= 0) {
        totalCents += cents;
      } else {
        hasTotalPrice = false;
      }
    });

    return {
      snapshot,
      prestations,
      totalMinutes,
      totalCents: hasTotalPrice ? totalCents : null,
    };
  }

  async handleLogout() {
    const { error } = await signOut();
    if (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  }

  toggleMobileMenu() {
    const nav = this.shadowRoot.querySelector('.nav-links');
    nav?.classList.toggle('active');
  }

  getOfferKey(offerInfo) {
    if (!offerInfo) return null;
    const startKey =
      offerInfo.start instanceof Date && !Number.isNaN(offerInfo.start.getTime())
        ? offerInfo.start.toISOString()
        : '';
    const endKey =
      offerInfo.end instanceof Date && !Number.isNaN(offerInfo.end.getTime())
        ? offerInfo.end.toISOString()
        : '';
    return [
      offerInfo.raw?.id || offerInfo.categorieId || offerInfo.typeOffre || 'offre',
      startKey,
      endKey,
    ].join(':');
  }

  rememberOfferExclusion(prestationId, offerInfo) {
    const key = this.getOfferKey(offerInfo);
    if (!key) return;
    try {
      const raw = window.sessionStorage.getItem('offerExcludedIds');
      const parsed = raw ? JSON.parse(raw) : null;
      const ids =
        parsed?.key === key && Array.isArray(parsed.ids)
          ? parsed.ids.map(String)
          : [];
      if (!ids.includes(String(prestationId))) {
        ids.push(String(prestationId));
      }
      window.sessionStorage.setItem('offerExcludedIds', JSON.stringify({ key, ids }));
    } catch {}
  }

  updateActiveLink() {
    const currentPath = getCurrentPath();
    const links = this.shadowRoot.querySelectorAll('[data-route]');

    links.forEach((link) => {
      if (link.dataset.route === currentPath) {
        link.classList.add('active');
        link.setAttribute('aria-current', 'page');
      } else {
        link.classList.remove('active');
        link.removeAttribute('aria-current');
      }
    });
  }

  handleOutsideClick(event) {
    const nav = this.shadowRoot && this.shadowRoot.querySelector('.nav-links');
    if (!nav || !nav.classList.contains('active')) return;

    // Si le clic se produit à l'intérieur du composant nav-bar (dans le shadow DOM
    // ou via le chemin d'événement composé), ne rien faire
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(this)) return;

    nav.classList.remove('active');
  }

  render() {
    const { isAuthenticated, userProfile } = getState();
    const currentPath = getCurrentPath();
    const cart = this.getCartSnapshot();
    const cartCount = Array.isArray(cart.prestations) ? cart.prestations.length : 0;
    const cartSummary = this.getCartSummaryData();
    const showSummary = this._showCartOverlay && cartSummary.prestations.length > 0;
    const offerRefDate = cartSummary.snapshot?.creneau?.debut || undefined;
    const offerState = showSummary
      ? getOfferSelectionState(
          cartSummary.prestations || [],
          this.activeOffres,
          offerRefDate
        )
      : {
          offerInfo: null,
          offeredIds: new Set(),
          autoAddedOfferedIds: new Set(),
          remaining: null,
          appliedOffers: [],
        };
    
    const offeredIds = offerState.offeredIds || new Set();
    const offerInfo = offerState.offerInfo || null;
    const offerSummaryMessage = getOfferSummaryMessage({
      remaining: offerState.remaining,
      categorieLabel: offerInfo?.categorieLabel,
    });
    const offerAchieved =
      Number.isFinite(offerState.remaining) && offerState.remaining === 0;
    const summaryData = showSummary
      ? buildBookingSummaryData({
          prestations: cartSummary.prestations || [],
          offeredIds,
          snapshot: cartSummary.snapshot,
          showEmployee: true,
          showCreneau: true,
          offerInfo,
          offerMessage: offerSummaryMessage,
          offerAchieved,
          appliedOffers: offerState.appliedOffers, // NOUVEAU
        })
      : { items: [], totalsHtml: '', extraHtml: '' };

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
        }

        *,
        *::before,
        *::after {
          box-sizing: border-box;
        }

        nav {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-md) var(--space-xl);
          max-width: var(--container-xl);
          margin: 0 auto;
        }

        .logo {
          font-size: var(--font-size-2xl);
          font-weight: var(--font-weight-bold);
          font-family: var(--font-family-serif, serif);
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--color-text);
          cursor: pointer;
          user-select: none;
          display: inline-flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .logo img {
          height: 64px;
          width: auto;
          display: block;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          list-style: none;
          margin: 0;
          padding: 0;
          flex-wrap: nowrap;
        }

        .nav-links a {
          color: var(--color-text);
          font-weight: var(--font-weight-medium);
          padding: calc(var(--space-xs)) calc(var(--space-sm));
          font-size: calc(var(--font-size-base, 1rem));
          border-radius: var(--radius-md);
          transition: color var(--transition-fast);
          cursor: pointer;
          text-decoration: none;
          white-space: nowrap;
        }

        .nav-login {
          display: none;
        }

        .nav-links a:hover {
          color: var(--color-primary);
        }

        .nav-links a.active {
          color: var(--color-primary);
          background-color: transparent;
          border-bottom: 2px solid var(--color-primary);
        }

        .right-section {
          display: flex;
          align-items: center;
          gap: var(--space-lg);
          min-width: 0;
        }

        .cart-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: var(--radius-full);
          background-color: var(--color-surface);
          border: 1px solid var(--color-border);
          cursor: pointer;
          color: var(--color-text);
          transition: all var(--transition-fast);
        }

        .cart-btn:hover {
          background-color: var(--color-bg);
          border-color: var(--color-primary-light);
          color: var(--color-primary-dark);
        }

        .cart-badge {
          position: absolute;
          top: -6px;
          right: -6px;
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          border-radius: 999px;
          background-color: var(--color-primary);
          color: var(--color-text-inverse);
          font-size: 12px;
          line-height: 18px;
          text-align: center;
          border: 2px solid var(--color-surface);
          box-sizing: border-box;
        }

        .user-info {
          text-align: right;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: var(--space-xxs, 0.15rem);
        }

        .user-name {
          font-weight: var(--font-weight-bold);
          font-size: var(--font-size-sm);
          color: var(--color-text);
        }

        .auth-actions a {
          text-decoration: none;
          font-size: calc(var(--font-size-base, 1rem) * 0.8);
          padding: calc(var(--space-xs) ) calc(var(--space-sm));
          color: var(--admin-primary);
        }

        .account-link {
          display: inline-flex;
          align-items: center;
          gap: var(--space-xs);
          padding: calc(var(--space-xs)) calc(var(--space-md));
          background-color: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          font-size: calc(var(--font-size-sm) * 0.9);
          color: var(--color-text);
          text-decoration: none;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .account-link:hover {
          background-color: var(--color-primary-dark);
          color: var(--color-text-inverse);
          border-color: var(--color-primary-light);
        }

        .account-link svg {
          flex-shrink: 0;
        }

        .account-text {
          display: inline;
        }

        .menu-toggle {
          display: none;
          background: none;
          border: none;
          font-size: var(--font-size-2xl);
          cursor: pointer;
          color: var(--color-text);
          width: 40px;
          height: 40px;
          padding: 0;
          line-height: 1;
          display: none;
          align-items: center;
          justify-content: center;
        }

        /* Écrans intermédiaires : réduire la taille du menu */
        @media (max-width: 1100px) {
          nav {
            padding: var(--space-md) var(--space-md);
          }

          .nav-links {
            gap: var(--space-sm);
          }

          .nav-links a {
            font-size: calc(var(--font-size-sm));
            padding: calc(var(--space-xs)) calc(var(--space-xs));
          }

          .logo img {
            height: 48px;
          }

          .logo span {
            font-size: var(--font-size-xl);
          }

          .right-section {
            gap: var(--space-md);
          }

          .logout-btn {
            padding: calc(var(--space-xs)) calc(var(--space-sm));
          }
        }

        /* Écrans plus petits : passer en menu hamburger */
        @media (max-width: 900px) {
          nav {
            position: relative;
            justify-content: flex-start;
            gap: var(--space-md);
          }

          .logo img {
            height: 48px;
          }

          .right-section {
            margin-left: auto;
            gap: var(--space-sm);
          }

          .account-link {
            max-width: min(60vw, 240px);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
          }

          .menu-toggle {
            flex-shrink: 0;
          }

          .nav-links {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            flex-direction: column;
            background-color: var(--color-surface);
            padding: var(--space-md);
            box-shadow: var(--shadow-lg);
            transform: translateY(-100%);
            opacity: 0;
            pointer-events: none;
            transition: all var(--transition-base);
          }

          .nav-links.active {
            transform: translateY(0);
            opacity: 1;
            pointer-events: auto;
          }

          .nav-links li {
            border-bottom: 1px solid var(--color-border);
          }

          .nav-links li:last-child {
            border-bottom: none;
          }

          .nav-links a {
            display: block;
            font-size: var(--font-size-base);
            padding: var(--space-md) var(--space-lg);
            min-height: 48px;
            line-height: 1.5;
          }

          .nav-login {
            display: block;
          }

          .menu-toggle {
            display: block;
            display: inline-flex;
          }

          .logout-btn {
            padding: calc(var(--space-xs)) calc(var(--space-sm));
            font-size: var(--font-size-xs);
          }
        }

        @media (max-width: 480px) {
          nav {
            padding: var(--space-sm) var(--space-sm);
          }

          .logo img {
            height: 40px;
          }

          .account-text {
            display: none;
          }

          .account-link {
            width: 40px;
            height: 40px;
            padding: 0;
            justify-content: center;
          }

          .user-name {
            display: none;
          }
        }

        ${getBookingSummaryStyles()}
      </style>

      <nav role="navigation" aria-label="Navigation principale">
        <div class="logo" data-route="/" role="button" tabindex="0">
          <img src="${logoEdena}" alt="Logo Edena Institut" />
          <span>Edena</span>
        </div>

        <ul class="nav-links">
          <li><a href="#/" data-route="/" class="${currentPath === '/' ? 'active' : ''}">${t('nav.home')}</a></li>
          <li><a href="#/booking" data-route="/booking" class="${currentPath === '/booking' ? 'active' : ''}">Prestations</a></li>
          <li><a href="#/offres" data-route="/offres" class="${currentPath === '/offres' ? 'active' : ''}">Offres</a></li>
          <li><a href="#/abonnements" data-route="/abonnements" class="${currentPath === '/abonnements' ? 'active' : ''}">Abonnements</a></li>
          <li><a href="#/team" data-route="/team" class="${currentPath === '/team' ? 'active' : ''}">${t('nav.team')}</a></li>
          <li><a href="#/faq" data-route="/faq" class="${currentPath === '/faq' ? 'active' : ''}">FAQ</a></li>
        </ul>

        <div class="right-section">
        ${
          cartCount > 0
            ? `
          <button class="cart-btn" data-action="open-cart" aria-label="Voir votre réservation en cours">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="8" cy="21" r="1"></circle>
              <circle cx="19" cy="21" r="1"></circle>
              <path d="M2 2h4l2.4 12.4a2 2 0 0 0 2 1.6h7.2a2 2 0 0 0 2-1.6L22 6H6"></path>
            </svg>
            <span class="cart-badge">${cartCount}</span>
          </button>
        `
            : ''
        }
        ${
          isAuthenticated
            ? `
          <div class="user-info">
            <a href="#/account" class="account-link" data-route="/account">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
              <span class="account-text">${userProfile?.prenom || 'Mon compte'}</span>
            </a>
          </div>
        `
            : `
          <div class="auth-actions">
            <a href="#/account" data-route="/account" class="${currentPath === '/account' ? 'active' : ''}">${t('account.login')}</a>
          </div>
        `
        }
        </div>

        <button class="menu-toggle" aria-label="Toggle menu" aria-expanded="false">
          ☰
        </button>
      </nav>

      ${renderBookingSummaryOverlay({
        isOpen: showSummary,
        items: summaryData.items,
        totalsHtml: summaryData.totalsHtml,
        extraHtml: summaryData.extraHtml,
      })}
    `;

    this.updateActiveLink();
  }
}

customElements.define('nav-bar', NavBar);
